// Ai-POS · Entrar con huella o Face ID (WebAuthn). La huella nunca sale del equipo:
// el teléfono firma un desafío del servidor y Ai-POS solo verifica la firma.
import { api, publicApi, guardarSesion, REFRESH_KEY } from "./api";

const b64uToBytes = (s) => {
  const b = atob(String(s).replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
};
const bytesToB64u = (buf) => {
  const u = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const opcionesCrear = (o) => ({
  ...o,
  challenge: b64uToBytes(o.challenge),
  user: { ...o.user, id: b64uToBytes(o.user.id) },
  excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBytes(c.id) })),
});
const opcionesPedir = (o) => ({
  ...o,
  challenge: b64uToBytes(o.challenge),
  allowCredentials: (o.allowCredentials || []).map((c) => ({ ...c, id: b64uToBytes(c.id) })),
});
const respuestaRegistro = (c) => ({
  id: c.id, rawId: bytesToB64u(c.rawId), type: c.type,
  response: {
    clientDataJSON: bytesToB64u(c.response.clientDataJSON),
    attestationObject: bytesToB64u(c.response.attestationObject),
  },
  clientExtensionResults: c.getClientExtensionResults(),
  authenticatorAttachment: c.authenticatorAttachment || undefined,
});
const respuestaFirma = (c) => ({
  id: c.id, rawId: bytesToB64u(c.rawId), type: c.type,
  response: {
    clientDataJSON: bytesToB64u(c.response.clientDataJSON),
    authenticatorData: bytesToB64u(c.response.authenticatorData),
    signature: bytesToB64u(c.response.signature),
    userHandle: c.response.userHandle ? bytesToB64u(c.response.userHandle) : null,
  },
  clientExtensionResults: c.getClientExtensionResults(),
  authenticatorAttachment: c.authenticatorAttachment || undefined,
});

/** ¿Este equipo puede usar huella / Face ID para entrar? */
export async function huellaDisponible() {
  try {
    if (!window.PublicKeyCredential || !window.isSecureContext) return false;
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) {
    return false;
  }
}

/** Activa la huella en este equipo (requiere sesión iniciada). */
export const activarHuella = async (nombre) => {
  const { data } = await api.post("/passkeys/registro/opciones");
  const cred = await navigator.credentials.create({ publicKey: opcionesCrear(data.opciones) });
  const r = await api.post("/passkeys/registro/verificar", {
    reto_id: data.reto_id, credential: respuestaRegistro(cred), nombre,
  });
  return r.data;
};

/** Entra con la huella en vez de la contraseña. */
export async function entrarConHuella() {
  const { data } = await publicApi.post("/passkeys/login/opciones");
  const cred = await navigator.credentials.get({ publicKey: opcionesPedir(data.opciones) });
  const r = await publicApi.post("/passkeys/login/verificar", {
    reto_id: data.reto_id, credential: respuestaFirma(cred),
  });
  guardarSesion(r.data);
  return r.data;
}

/** Desbloquea la app al abrirla: sin esta firma no se entrega ningún token. */
export async function desbloquearConHuella() {
  const { data } = await publicApi.post("/passkeys/login/opciones");
  const cred = await navigator.credentials.get({ publicKey: opcionesPedir(data.opciones) });
  const r = await publicApi.post("/passkeys/desbloquear", {
    reto_id: data.reto_id,
    credential: respuestaFirma(cred),
    refresh_token: localStorage.getItem(REFRESH_KEY) || null,
  });
  guardarSesion(r.data);
  return r.data;
}

export const estadoHuella = () => api.get("/passkeys/estado").then((r) => r.data);
export const listarHuellas = () => api.get("/passkeys").then((r) => r.data.huellas);
export const quitarHuella = (id) => api.delete(`/passkeys/${encodeURIComponent(id)}`).then((r) => r.data);
export const setBloqueoApertura = (activo) => api.post("/passkeys/bloqueo", { activo }).then((r) => r.data);

export const listarDispositivos = (todos = false) =>
  api.get("/auth/devices", { params: todos ? { todos: true } : {} }).then((r) => r.data);
export const cerrarDispositivo = (familia) => api.delete(`/auth/devices/${familia}`).then((r) => r.data);
export const cerrarOtrosDispositivos = () => api.post("/auth/devices/cerrar-los-demas").then((r) => r.data);
export const renombrarDispositivo = (familia, nombre) =>
  api.patch(`/auth/devices/${familia}`, { nombre }).then((r) => r.data);
